import React, { createContext, useContext, useEffect, useReducer } from 'react';
import { auth, db } from '../firebase';
import { 
  onAuthStateChanged, 
  signOut, 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile
} from 'firebase/auth';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';

// Initial state
const initialState = {
  user: null,
  isAuthenticated: false,
  loading: true,
  error: null,
  theme: 'light'
};

// Action types
export const ACTION_TYPES = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_USER: 'SET_USER',
  LOGOUT: 'LOGOUT',
  SET_THEME: 'SET_THEME',
  CLEAR_ERROR: 'CLEAR_ERROR'
};

// Reducer function
const appReducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };
    case ACTION_TYPES.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false
      };
    case ACTION_TYPES.SET_USER:
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
        loading: false,
        error: null
      };
    case ACTION_TYPES.LOGOUT:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        loading: false,
        error: null
      };
    case ACTION_TYPES.SET_THEME:
      return {
        ...state,
        theme: action.payload
      };
    case ACTION_TYPES.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };
    default:
      return state;
  }
};

// Create context
const AppContext = createContext();

// AppProvider component
export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Subscribe to Firebase auth state and Firestore user document
  useEffect(() => {
    let userDocUnsubscribe = null;

    const authUnsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      // Clean up previous Firestore listener
      if (userDocUnsubscribe) {
        userDocUnsubscribe();
        userDocUnsubscribe = null;
      }

      if (firebaseUser) {
        const { uid, displayName, email } = firebaseUser;
        
        // Set up real-time listener for user document in Firestore
        const userDocRef = doc(db, 'users', uid);
        userDocUnsubscribe = onSnapshot(
          userDocRef, 
          (docSnapshot) => {
            if (docSnapshot.exists()) {
              const userData = docSnapshot.data();
              dispatch({
                type: ACTION_TYPES.SET_USER,
                payload: { 
                  uid, 
                  displayName: userData.displayName || displayName, 
                  email,
                  organizationId: userData.organizationId || null,
                  role: userData.role || null,
                  ...userData
                }
              });
            } else {
              // User document doesn't exist yet (during account creation)
              dispatch({
                type: ACTION_TYPES.SET_USER,
                payload: { uid, displayName, email, organizationId: null }
              });
            }
          },
          (error) => {
            console.error('Error listening to user document:', error);
            // Fallback to basic user info
            dispatch({
              type: ACTION_TYPES.SET_USER,
              payload: { uid, displayName, email, organizationId: null }
            });
          }
        );
      } else {
        dispatch({ type: ACTION_TYPES.LOGOUT });
      }
    });

    return () => {
      authUnsubscribe();
      if (userDocUnsubscribe) {
        userDocUnsubscribe();
      }
    };
  }, []);

  // Action creators
  const setLoading = (loading) => {
    dispatch({ type: ACTION_TYPES.SET_LOADING, payload: loading });
  };

  const setError = (error) => {
    dispatch({ type: ACTION_TYPES.SET_ERROR, payload: error });
  };

  const setUser = (user) => {
    dispatch({ type: ACTION_TYPES.SET_USER, payload: user });
  };

  const login = async (email, password) => {
    dispatch({ type: ACTION_TYPES.SET_LOADING, payload: true });
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // The onAuthStateChanged listener will handle setting the user
    } catch (error) {
      dispatch({ type: ACTION_TYPES.SET_ERROR, payload: error.message });
      throw error;
    }
  };

  const createAccount = async (email, password, displayName) => {
    dispatch({ type: ACTION_TYPES.SET_LOADING, payload: true });
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const { user } = userCredential;

      // Update profile with display name
      await updateProfile(user, { displayName });

      // Create user document in Firestore
      await setDoc(doc(db, 'users', user.uid), {
        displayName,
        email: user.email,
        organizationId: null,
        createdAt: new Date(),
      });

      // The onAuthStateChanged listener will handle setting the user
      return userCredential;
    } catch (error) {
      dispatch({ type: ACTION_TYPES.SET_ERROR, payload: error.message });
      throw error;
    }
  };

  const logout = async () => {
    await signOut(auth);
  };

  const setTheme = (theme) => {
    dispatch({ type: ACTION_TYPES.SET_THEME, payload: theme });
  };

  const clearError = () => {
    dispatch({ type: ACTION_TYPES.CLEAR_ERROR });
  };

  const value = {
    ...state,
    setLoading,
    setError,
    setUser,
    login,
    createAccount,
    logout,
    setTheme,
    clearError
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

// Custom hook to use the AppContext
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export default AppContext;
